<template>
    <ul class="status-key list-inline">
        <!-- Advertiser-specific: Creative Changes Required -->
        <li v-if="isAdvertiser && hasIcon('change_requested')" class="list-inline-item">
            <component
                :is="withLinks ? 'a' : 'span'"
                :href="withLinks ? approvalUrl : null"
                :class="withLinks ? 'text-plain d-flex align-items-center' : ''"
            >
                <span class="icon-status icon-pending" style="font-size: .7rem;">
                    <i class="fa fa-exclamation text-danger"></i>
                </span>
                Creative Changes Required
                <span v-if="counts.creative_change_required_count" class="ml-1 badge badge-danger badge-pill">
                    {{ counts.creative_change_required_count }}
                </span>
            </component>
        </li>

        <!-- Non-advertiser: Approval Needed -->
        <li v-if="!isAdvertiser && hasIcon('approval_needed')" class="list-inline-item">
            <component
                :is="withLinks ? 'a' : 'span'"
                :href="withLinks ? approvalUrl : null"
                :class="withLinks ? 'text-plain d-flex align-items-center' : ''"
            >
                <span class="icon-status icon-pending" style="font-size: .7rem;">
                    <i class="fa fa-exclamation text-danger"></i>
                </span>
                Approval<br>Needed
                <span v-if="counts.approval_needed_count" class="ml-1 badge badge-danger badge-pill">
                    {{ counts.approval_needed_count }}
                </span>
            </component>
        </li>

        <!-- Non-advertiser: Creative Changes Requested -->
        <li v-if="!isAdvertiser && hasIcon('change_requested')" class="list-inline-item">
            <span class="icon-status icon-running"
                  style="background-color: #ffffff; border: 1px solid #109DEA; color: #109DEA; font-size: .7rem">
                <i class="fal fa-chevron-double-right" style="font-weight: 400;"></i>
            </span>
            Creative Changes<br>Requested
        </li>

        <!-- Non-advertiser: Approved -->
        <li v-if="!isAdvertiser && hasIcon('approved')" class="list-inline-item">
            <span class="icon-status icon-thumbs-up"
                  style="border: 1px solid #35647e; background: #fff; color: #35647E; font-size: .7rem">
                <i class="fal fa-thumbs-up"></i>
            </span>
            Approved
        </li>

        <!-- Common icons -->
        <li v-if="hasIcon('complete')" class="list-inline-item">
            <span class="icon-status icon-check"
                  style="font-size: .8rem; color: #fff; border-color: #1BA66A; background: #1BA66A;">
                <i class="fal fa-check fa-fw" style="font-weight: 400"></i>
            </span>
            Completed
        </li>

        <li v-if="hasIcon('verified')" class="list-inline-item">
            <span class="icon-status icon-ribbon"
                  style="border: 1px solid #35647e; background: #fff; color: #35647e; font-size: .7rem;">
                <i class="fal fa-award fa-fw"></i>
            </span>
            Fulfilled
        </li>

        <li v-if="hasIcon('unfulfilled')" class="list-inline-item">
            <span class="icon-status icon-denied" style="background: #fff; border: 1px solid #e8eef2; font-size: .7rem">
                <i class="fa fa-times text-danger"></i>
            </span>
            Unfulfilled
        </li>

        <li v-if="hasIcon('paid')" class="list-inline-item">
            <span class="icon-status icon-paid"
                  style="border: 1px solid #35647e; background: #fff; color: #35647E; font-size: .7rem">
                <i class="fa fa-dollar-sign fa-fw"></i>
            </span>
            Paid
        </li>

        <li v-if="hasIcon('denied')" class="list-inline-item">
            <span class="icon-status icon-denied bg-danger" style="border-color: #d11f2c;font-size: .7rem">
                <i class="fa fa-times text-white"></i>
            </span>
            Denied
        </li>

        <li v-if="hasIcon('expired')" class="list-inline-item">
            <span class="icon-status icon-expired bg-danger" style="border-color: #d11f2c;font-size: .7rem">
                <i class="fa fa-clock text-white"></i>
            </span>
            Expired
        </li>

        <li v-if="hasIcon('shared')" class="list-inline-item">
            <span class="icon-status icon-shared" style="border: 1px solid #35647e; background: #fff; font-size: .7rem">
                <i class="fal fa-share-alt fa-fw"></i>
            </span>
            Shared
        </li>
    </ul>
</template>

<script>
import axios from 'axios';

export default {
    name: 'OrderStatusKey',

    props: {
        icons: {
            type: Array,
            default: () => [],
        },
        withLinks: {
            type: Boolean,
            default: true,
        },
        userRole: {
            type: String,
            required: true,
            validator: (value) => ['admin', 'team', 'advertiser'].includes(value),
        },
    },

    data() {
        return {
            counts: {
                approval_needed_count: 0,
                creative_change_required_count: 0,
            },
            loading: false,
        };
    },

    computed: {
        isAdvertiser() {
            return this.userRole === 'advertiser';
        },

        isTeam() {
            return this.userRole === 'team';
        },

        isAdmin() {
            return this.userRole === 'admin';
        },

        approvalUrl() {
            if (this.isTeam) {
                return '/team/orders/search?approval_needed=true';
            }
            if (this.isAdmin) {
                return '/admin/orders/search?approval_needed=true';
            }
            return '/advertiser/orders/search?approval_needed=true';
        },

        filteredIcons() {
            const validIcons = [
                'approval_needed',
                'denied',
                'expired',
                'paid',
                'verified',
                'complete',
                'change_requested',
                'unfulfilled',
                'shared',
                'approved',
            ];
            return this.icons.filter(icon => validIcons.includes(icon));
        },
    },

    mounted() {
        this.fetchCounts();
        this._creativeReviewClosed = () => this.fetchCounts();
        window.addEventListener('creative-review-closed', this._creativeReviewClosed);
    },

    beforeDestroy() {
        window.removeEventListener('creative-review-closed', this._creativeReviewClosed);
    },

    methods: {
        hasIcon(iconName) {
            return this.filteredIcons.includes(iconName);
        },

        async fetchCounts() {
            if (this.loading) {
                return;
            }

            this.loading = true;

            try {
                const response = await axios.get('/api/orders/status-counts');
                this.counts = response.data.data;
            } catch (error) {
                console.error('Failed to fetch order status counts:', error);
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
